// Client-side compression (Invariant 4). A 12MP phone JPEG is ~4MB; the venue
// uplink is the scarcest resource on event night, so nothing leaves the device
// at full size.

export const WEB_MAX_EDGE = 1600;
export const THUMB_MAX_EDGE = 320;
const WEB_QUALITY = 0.82;
const THUMB_QUALITY = 0.7;

export interface Compressed {
  web: Blob;
  thumb: Blob;
  contentType: "image/jpeg";
}

/** Longest-edge fit, never upscaling a photo that is already small. */
function fit(w: number, h: number, maxEdge: number): { w: number; h: number } {
  const longest = Math.max(w, h);
  if (longest <= maxEdge) return { w, h };
  const scale = maxEdge / longest;
  return { w: Math.round(w * scale), h: Math.round(h * scale) };
}

async function decode(file: Blob): Promise<ImageBitmap> {
  // `from-image` applies EXIF orientation. Without it, portrait shots from
  // phones arrive sideways in the gallery.
  return await createImageBitmap(file, { imageOrientation: "from-image" });
}

function canvasFor(w: number, h: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

async function toBlob(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  quality: number,
): Promise<Blob> {
  if ("convertToBlob" in canvas) {
    return await canvas.convertToBlob({ type: "image/jpeg", quality });
  }
  return await new Promise<Blob>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
      "image/jpeg",
      quality,
    );
  });
}

async function render(
  bitmap: ImageBitmap,
  maxEdge: number,
  quality: number,
): Promise<Blob> {
  const { w, h } = fit(bitmap.width, bitmap.height, maxEdge);
  const canvas = canvasFor(w, h);
  const ctx = canvas.getContext("2d") as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error("2d context unavailable");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return await toBlob(canvas, quality);
}

/**
 * Produces the web image and its thumbnail in one decode.
 *
 * Both are JPEG regardless of input: the gallery grid is photographic, and one
 * predictable output type keeps the storage paths and MIME allow-list simple.
 */
export async function compress(file: Blob): Promise<Compressed> {
  const bitmap = await decode(file);
  try {
    // Sequential, not parallel: two large canvases at once is how mid-range
    // phones OOM during a burst.
    const web = await render(bitmap, WEB_MAX_EDGE, WEB_QUALITY);
    const thumb = await render(bitmap, THUMB_MAX_EDGE, THUMB_QUALITY);
    return { web, thumb, contentType: "image/jpeg" };
  } finally {
    bitmap.close();
  }
}
